import { makeValuesPredicate } from '@sgme/fp';

import type { OptionSide } from '@/store/option/state.ts';
import type { AlgoOrderType as OMSAlgoOrderType, OrderType as OrderTypeFromOMS } from '@/store/orders/state.ts';
import type { Currency, CurrencyPair } from '@/types/currency.ts';
import type { ISODate, ISODateTime } from '@/types/date.ts';

export type BlotterOrderEntryFromBackend = {
  //
  // technical fields and discriminants
  //
  id: string;
  type: 'BLOTTER.ORDER';
  updateTime: ISODateTime; // the time when the order was last updated, in ISO 8601 format

  // who
  account: string; // encode a number, > 0, base 10

  // what
  orderType: OrderType;
  status: BlotterOrderStatus;

  currencyPair: CurrencyPair;
  yourWay: OptionSide | null;

  notional: number | null;
  notionalCurrency: Currency;
  executedAmount: number | null;
  marginInBps: number | null;
  margin: number | null;
  price: number | null;

  limitPrice: number;
  customerPrice: number | null;
  executionPrice: number | null;

  expiryDay: ISODate | null; // YYYY-MM-DD
  expiryTime: string | null; // hh:mm

  startTime: ISODateTime | null;
  endTime: ISODateTime | null;

  maturityDate: ISODateTime;

  liquidityPool: string | null;
  clippingMode: string | null;
  clipSize: number | null;

  swapPoints: number | null;
  forwardPrice: number | null;

  remainingAmount: number | null;
  noWorseThan: number | null;
  averagePrice: number | null;
  speed: string | null;

  fixingBenchmark: string | null;
  fixingPlace: string | null;
  fixingTime: string | null; // hh:mm:ss
  fixingDateUtc: ISODate | null; // YYYY-MM-DD
  fixingPriceType: string | null;
  fixingMarginType: string | null;

  // flags
  goodTillCancelled: boolean; // sometime referred as 'gtc' in legacy code;
  randomize: boolean | null;
  alphaSeeker: boolean | null;
  spreadCapture: boolean | null;

  isGtc: boolean;
  isNdf: boolean;
};

//
// ORDER STATUSES
//
export const finalBlotterOrderStatuses = [
  'Done',
  'Finished',
  'Cancelled',
  'Expired',
  'Partially done',
  'InError',
  'Rejected',
] as const;

export type BlotterOrderStatusFinal = (typeof finalBlotterOrderStatuses)[number];

const blotterOrderStatuses = [
  'Submitted',
  'Watched',
  'Executing',
  'Paused',
  'Updating',
  'Cancelling',
  'Running',
  'New',
  ...finalBlotterOrderStatuses,
] as const;

export type BlotterOrderStatus = (typeof blotterOrderStatuses)[number];

export const blotterOrderStatusIsFinal = makeValuesPredicate(finalBlotterOrderStatuses);

//
// ORDER TYPES
//
export const nonAlgoOrderTypes = ['StopLoss', 'TakeProfit', 'Call', 'Fixing', 'Lyrebird'] as const;
export const algoOrderTypes = ['Twap', 'Nightjar', 'Falcon'] as const;

export type NonAlgoOrderType = (typeof nonAlgoOrderTypes)[number];
export type AlgoOrderType = (typeof algoOrderTypes)[number];
export type OrderType = NonAlgoOrderType | AlgoOrderType;

export const isAlgoOrderType = makeValuesPredicate(algoOrderTypes);

export const OMSOrderTypeFromBlotterOrderType: { [type in Exclude<OrderType, 'Lyrebird'>]: OrderTypeFromOMS } = {
  StopLoss: 'StopLossOrder',
  TakeProfit: 'TakeProfitOrder',
  Call: 'CallOrder',
  Fixing: 'FixingOrder',
  Twap: 'Twap+Order',
  Nightjar: 'NightjarOrder',
  Falcon: 'FalconOrder',
};

export const OMSOrderTypeFromBlotterAlgoOrderType: { [type in AlgoOrderType]: OMSAlgoOrderType } = {
  Twap: 'Twap+Order',
  Nightjar: 'NightjarOrder',
  Falcon: 'FalconOrder',
};
